<template>
  <div class="my-training">

    <div class="page-header">
      <div>
        <h5 class="page-title">My Training</h5>
        <p class="page-sub">Track your assigned courses, certificates and learning history</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tab-row">
      <button v-for="t in tabs" :key="t.key"
        :class="['tab-btn', activeTab === t.key ? 'tab-btn--active' : '']"
        @click="switchTab(t.key)"
      >
        {{ t.label }}
        <span v-if="t.key === 'overdue' && overdueCount > 0" class="tab-badge">{{ overdueCount }}</span>
      </button>
    </div>

    <div v-if="loading" class="full-loader"><div class="spinner"></div></div>

    <!-- Course cards -->
    <div v-else-if="activeTab !== 'catalogue'" class="card-grid">
      <div v-if="!enrolments.length" class="empty-state">
        <font-awesome-icon icon="graduation-cap" class="empty-icon" />
        <p>{{ emptyMessage }}</p>
        <button v-if="activeTab === 'todo'" class="btn-outline" @click="switchTab('catalogue')">Browse Catalogue</button>
      </div>
      <div v-for="e in enrolments" :key="e.id" class="course-card" @click="openCourse(e)">
        <div class="course-card-top">
          <span :class="['type-badge', 'type-badge--' + e.course?.course_type]">
            {{ e.course?.course_type }}
          </span>
          <span :class="['status-dot', 'status-dot--' + e.status]"></span>
        </div>
        <h4 class="course-title">{{ e.course?.title }}</h4>
        <p class="course-meta">{{ e.course?.est_minutes ? e.course.est_minutes + ' min' : '' }}</p>
        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: e.progress_pct + '%' }"></div>
        </div>
        <div class="card-footer">
          <span class="progress-label">{{ e.progress_pct }}% complete</span>
          <span v-if="e.due_date" :class="['due-badge', dueBadgeClass(e.due_date, e.status)]">
            Due {{ formatDate(e.due_date) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Catalogue tab -->
    <div v-else class="catalogue-list">
      <input v-model="catalogueSearch" class="filter-input" placeholder="Search catalogue…" @input="loadCatalogue" />
      <div v-if="catalogueLoading" class="full-loader"><div class="spinner"></div></div>
      <div v-else class="card-grid">
        <div v-if="!catalogue.length" class="empty-state">
          <p>No optional courses available at this time.</p>
        </div>
        <div v-for="c in catalogue" :key="c.id" class="course-card">
          <div class="course-card-top">
            <span class="type-badge type-badge--optional">Optional</span>
            <span v-if="c.category" class="cat-badge">{{ c.category?.name }}</span>
          </div>
          <h4 class="course-title">{{ c.title }}</h4>
          <p class="course-meta">{{ c.est_minutes ? c.est_minutes + ' min' : '' }}</p>
          <p class="course-desc">{{ c.description?.substring(0, 100) }}{{ c.description?.length > 100 ? '…' : '' }}</p>
          <button class="btn-enrol" @click="requestEnrolment(c)">
            {{ c.requires_approval ? 'Request Access' : 'Enrol Now' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Certificates quick view -->
    <div v-if="activeTab === 'completed' && certificates.length" class="certs-section">
      <h6 class="section-title">My Certificates</h6>
      <div class="cert-list">
        <div v-for="cert in certificates" :key="cert.id" class="cert-row">
          <font-awesome-icon icon="certificate" class="cert-icon" />
          <div class="cert-info">
            <div class="cert-name">{{ cert.enrolment?.course?.title }}</div>
            <div class="cert-meta">{{ cert.certificate_no }} · Issued {{ formatDate(cert.issued_at) }}
              <span v-if="cert.expires_at"> · Expires {{ formatDate(cert.expires_at) }}</span>
            </div>
          </div>
          <span :class="['cert-status', 'cert-status--' + cert.status]">{{ cert.status }}</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'MyTraining',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: false,
      catalogueLoading: false,
      activeTab: 'todo',
      enrolments: [],
      catalogue: [],
      certificates: [],
      catalogueSearch: '',
      overdueCount: 0,
      tabs: [
        { key: 'todo',        label: 'To Do' },
        { key: 'in_progress', label: 'In Progress' },
        { key: 'completed',   label: 'Completed' },
        { key: 'overdue',     label: 'Overdue' },
        { key: 'catalogue',   label: 'Catalogue' },
      ],
    };
  },
  computed: {
    emptyMessage() {
      return {
        todo:        'You have no pending training assignments.',
        in_progress: 'No courses in progress.',
        completed:   'No completed courses yet.',
        overdue:     'No overdue training. Great job!',
      }[this.activeTab] ?? '';
    },
  },
  created() {
    this.loadEnrolments();
    this.loadOverdueCount();
    this.loadCertificates();
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'catalogue') this.loadCatalogue();
      else this.loadEnrolments();
    },
    async loadEnrolments() {
      this.loading = true;
      try {
        const { data } = await axios.get('/training/me/enrolments', { params: { tab: this.activeTab } });
        this.enrolments = data.data?.data ?? data.data ?? [];
      } catch { this.toast.error('Failed to load training.'); }
      finally { this.loading = false; }
    },
    async loadOverdueCount() {
      try {
        const { data } = await axios.get('/training/me/enrolments', { params: { tab: 'overdue', per_page: 1 } });
        this.overdueCount = data.data?.total ?? 0;
      } catch { /* silent */ }
    },
    async loadCatalogue() {
      this.catalogueLoading = true;
      try {
        const { data } = await axios.get('/training/me/catalogue', { params: { search: this.catalogueSearch } });
        this.catalogue = data.data?.data ?? data.data ?? [];
      } catch { this.toast.error('Failed to load catalogue.'); }
      finally { this.catalogueLoading = false; }
    },
    async loadCertificates() {
      try {
        const { data } = await axios.get('/training/me/certificates');
        this.certificates = data.data ?? [];
      } catch { /* silent */ }
    },
    async requestEnrolment(course) {
      try {
        await axios.post('/training/me/enrolment-requests', { course_id: course.id });
        this.toast.success(course.requires_approval ? 'Enrolment request sent for approval.' : 'Enrolled successfully!');
        this.loadCatalogue();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to enrol.'); }
    },
    openCourse(enrolment) {
      this.$router.push(this.$url(`/hr/training/player/${enrolment.id}`));
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    daysLeft(d) { return d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null; },
    dueBadgeClass(d, status) {
      if (status === 'overdue') return 'due-badge--red';
      const n = this.daysLeft(d);
      if (n === null) return '';
      if (n <= 1)  return 'due-badge--red';
      if (n <= 7)  return 'due-badge--amber';
      return 'due-badge--green';
    },
  },
};
</script>

<style scoped>
.my-training { padding-bottom:32px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* Tabs */
.tab-row { display:flex; gap:4px; flex-wrap:wrap; border-bottom:1.5px solid #e4eaf2; margin-bottom:20px; }
.tab-btn { position:relative; background:none; border:none; padding:10px 18px; font-size:13px; font-weight:500; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-1.5px; }
.tab-btn:hover { color:#1a2b45; }
.tab-btn--active { color:#004AAD; border-bottom-color:#004AAD; }
.tab-badge { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; background:#ef4444; color:#fff; font-size:10px; font-weight:700; margin-left:5px; }

/* Cards */
.card-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:16px; }
.course-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px; cursor:pointer; transition:box-shadow .15s; }
.course-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.08); }
.course-card-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.course-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 4px; line-height:1.35; }
.course-meta  { font-size:12px; color:#7a90b0; margin:0 0 12px; }
.course-desc  { font-size:12px; color:#94a3b8; margin:0 0 12px; }

/* Progress bar */
.progress-bar-wrap { background:#f1f5f9; border-radius:99px; height:5px; margin-bottom:10px; }
.progress-bar-fill { background:#004AAD; height:5px; border-radius:99px; transition:width .4s; }

.card-footer { display:flex; align-items:center; justify-content:space-between; }
.progress-label { font-size:11.5px; color:#7a90b0; }

/* Type + status badges */
.type-badge { display:inline-block; padding:2px 9px; border-radius:20px; font-size:11px; font-weight:600; text-transform:capitalize; }
.type-badge--mandatory   { background:#fee2e2; color:#dc2626; }
.type-badge--recommended { background:#dbeafe; color:#1d4ed8; }
.type-badge--optional    { background:#dcfce7; color:#15803d; }
.cat-badge { font-size:11px; color:#94a3b8; }
.status-dot { width:9px; height:9px; border-radius:50%; }
.status-dot--not_started  { background:#94a3b8; }
.status-dot--in_progress  { background:#3b82f6; }
.status-dot--overdue      { background:#f97316; }
.status-dot--completed    { background:#22c55e; }
.status-dot--failed       { background:#ef4444; }

.due-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:2px 9px; }
.due-badge--green { background:#dcfce7; color:#15803d; }
.due-badge--amber { background:#fff7ed; color:#c2410c; }
.due-badge--red   { background:#fee2e2; color:#dc2626; }

/* Catalogue */
.catalogue-list { }
.filter-input { width:100%; padding:9px 14px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; margin-bottom:16px; }
.btn-enrol { margin-top:12px; width:100%; padding:8px 0; background:#004AAD; color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:500; cursor:pointer; }
.btn-enrol:hover { background:#0056d2; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }

/* Certificates */
.certs-section { margin-top:24px; }
.section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 12px; }
.cert-list { display:flex; flex-direction:column; gap:8px; }
.cert-row { display:flex; align-items:center; gap:14px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 16px; }
.cert-icon { font-size:22px; color:#004AAD; flex-shrink:0; }
.cert-info { flex:1; min-width:0; }
.cert-name { font-size:13px; font-weight:600; color:#1a2b45; }
.cert-meta { font-size:11.5px; color:#94a3b8; margin-top:2px; }
.cert-status { font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.cert-status--valid   { background:#dcfce7; color:#15803d; }
.cert-status--expired { background:#f1f5f9; color:#64748b; }
.cert-status--revoked { background:#fee2e2; color:#dc2626; }

/* Loader */
.full-loader { display:flex; align-items:center; justify-content:center; min-height:240px; }
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* Empty state */
.empty-state { grid-column:1/-1; text-align:center; padding:48px 20px; color:#94a3b8; }
.empty-icon { font-size:40px; color:#cbd5e1; margin-bottom:12px; }
</style>
